import React, { FC, useCallback, useMemo, useRef } from 'react'
import { useFormContext, FieldError, Controller } from 'react-hook-form'

import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material'

import clsx from 'clsx'
import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter'

const useStyles = makeStyles((theme: Theme) => ({
  inputContainer: {
    marginLeft: ({ ml }: InputFieldProps) => ml,
    marginRight: ({ mr }: InputFieldProps) => mr,
    marginBottom: ({ marginBottom }: InputFieldProps) => marginBottom || 20,
    width: ({ width }: InputFieldProps) => width || 300,
    maxHeight: ({ maximumHeight }) => maximumHeight || 63,
    '& .MuiOutlinedInput-root': {
      height: 40,
      '&.Mui-disabled': {
        '& fieldset': {
          borderColor: 'rgba(255,255,255,0.4)',
        },
      },
    },
    '& fieldset': {
      borderColor: ({ errorMessageText, borderColor }) =>
        errorMessageText ? theme.palette.error.main : borderColor || 'rgba(255,255,255,0.4)',
    },
  },
  inputContainerNoLabel: {
    maxHeight: ({ maximumHeight }) => maximumHeight || '40px !important',
    marginBottom: 0,
  },
  label: {
    fontSize: ({ fontSize }: InputFieldProps) => fontSize || 18,
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ width }: InputFieldProps) => width || 300,
    height: (multiline) => (multiline ? 'auto' : 40),
    boxSizing: 'border-box',
    '& .MuiInputBase-root': {
      fontSize: '14px !important',
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 20,
      paddingBottom: 20,
      boxSizing: 'border-box',
      '&.Mui-disabled': {
        color: '#FFFFFF',
        '-webkit-text-fill-color': '#FFFFFF',
      },
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      '-webkit-box-shadow': 'none',
    },
    '& :-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  prefixIcon: {
    fontSize: 18,
    fontWeight: 400,
    paddingRight: 4,
    color: theme.palette.primary.light,
    opacity: 0.5,
  },
}))

type InputType = {
  inputType:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
}
interface InputFieldProps extends InputType {
  labelName?: string
  inputName: string
  placeholder?: string
  errorMessageText?: string | FieldError
  required?: boolean
  isCompanyEmail?: boolean
  min?: number
  rows?: number
  multiline?: boolean
  defaultValue?: string
  width?: number | string
  maximumHeight?: number | string
  fontSize?: number | string
  ml?: number | string
  mr?: number | string
  borderColor?: string
  marginBottom?: number | string
  // eslint-disable-next-line
  onBlurInput?: any
  prefix?: string
  disabled?: boolean
}

export const InputField: FC<InputFieldProps> = ({
  labelName,
  inputName,
  inputType,
  placeholder,
  errorMessageText,
  min,
  rows,
  multiline,
  defaultValue = '',
  width = 300,
  maximumHeight,
  fontSize = 18,
  ml,
  mr,
  borderColor,
  marginBottom = 20,
  onBlurInput,
  prefix,
  disabled = false,
}) => {
  const { label, inputContainer, inputContainerNoLabel, formControl, prefixIcon } = useStyles({
    labelName,
    inputName,
    inputType,
    placeholder,
    errorMessageText,
    min,
    rows,
    multiline,
    defaultValue,
    width,
    maximumHeight,
    fontSize,
    ml,
    mr,
    borderColor,
    marginBottom,
    onBlurInput,
  })

  const inputEl = useRef<HTMLInputElement>(null)
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext()
  const error = errors[inputName]

  const errorMessage = useMemo(() => {
    const message = inputName !== 'amount' ? error?.message.replace(inputName, '') : error?.message
    return capitalizeFirstLetter(message)
  }, [error, inputName])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(inputName, event.target.value)
    },
    [inputName, setValue],
  )

  return (
    <Grid
      container
      item
      className={clsx(labelName ? inputContainer : [inputContainer, inputContainerNoLabel])}
      direction={'column'}
      wrap={'nowrap'}
    >
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}
      <Grid item>
        <FormControl size={'small'} className={formControl}>
          <Controller
            control={control}
            name={inputName}
            defaultValue={defaultValue || ''}
            // eslint-disable-next-line
            render={({ field: { onBlur } }) => (
              <OutlinedInput
                disabled={disabled}
                {...register(inputName, {
                  onChange: handleChange,
                })}
                inputProps={{
                  min: min || inputType === 'number' ? 0 : '',
                  style: { fontSize },
                }}
                error={!!error}
                multiline={multiline}
                placeholder={placeholder}
                inputRef={inputEl}
                type={inputType}
                defaultValue={defaultValue}
                id={inputName}
                color={'primary'}
                rows={rows}
                onBlur={() => {
                  onBlur()
                  if (onBlurInput) {
                    onBlurInput()
                  }
                }}
                startAdornment={prefix && <span className={clsx(prefixIcon, 'material-icons')}>{prefix}</span>}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item>
        {(error || errorMessageText) && (
          <FormHelperText id={`component-error-text-${inputName}`}>{errorMessage || errorMessageText}</FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}
